import { UNKNOWN_SECTION, type Section, type SectionConfig, type TargetType } from '../types';

const isInternalHostname = (
	hostname: string,
	internalHostnames: ReadonlyArray<string>
): boolean => {
	const host = hostname.toLowerCase().replace(/^www\./, '');

	return internalHostnames.some((internal) => host === internal || host.endsWith(`.${internal}`));
};

/**
 * Strips origin, query and hash, leaving a leading-slash path.
 *
 * Absolute urls on a foreign host resolve to `''` rather than to their path:
 * `https://medium.com/editions` must not be read as the Editions section just
 * because its path collides with one of our prefixes. `link_out` already covers
 * genuinely outbound clicks, so dropping them here loses nothing.
 */
const toPath = (href: string, internalHostnames: ReadonlyArray<string>): string => {
	const trimmed = href.trim();

	if (!trimmed) return '';

	try {
		if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('//')) {
			const url = new URL(trimmed.startsWith('//') ? `https:${trimmed}` : trimmed);

			return isInternalHostname(url.hostname, internalHostnames) ? url.pathname : '';
		}
	} catch {
		return '';
	}

	return trimmed.split(/[?#]/)[0];
};

/**
 * The section a path belongs to, or `other` when nothing matches (HB-967).
 *
 * The route map is passed in rather than held here: which paths make up a
 * "section" is site knowledge, and this package is shared across sites that
 * have entirely different ones.
 */
export const getSectionFromHref = (
	href: string | null | undefined,
	config: SectionConfig
): Section => {
	const path = toPath(href ?? '', config.internalHostnames);

	if (!path.startsWith('/')) return UNKNOWN_SECTION;

	// Longest prefix first, so `/private-sales` is not swallowed by `/primary`
	// and `/talks/podcasts` resolves ahead of a shorter `/talks`.
	const match = [...config.sectionPrefixes]
		.sort((a, b) => b.prefix.length - a.prefix.length)
		.find(({ prefix }) => path === prefix || path.startsWith(`${prefix}/`));

	return match?.section ?? UNKNOWN_SECTION;
};

/**
 * What kind of thing a path points at.
 *
 * Derived from path depth rather than passed in, so that the common case — a
 * card or nav link that already knows its href — needs no extra tagging. Call
 * sites that know better (a collection tile, say) pass `targetType` explicitly
 * to `trackOnwardClick` and override this.
 */
export const getTargetTypeFromHref = (
	href: string | null | undefined,
	config: SectionConfig
): TargetType => {
	const path = toPath(href ?? '', config.internalHostnames);
	const section = getSectionFromHref(href, config);

	if (section === UNKNOWN_SECTION) return 'other';
	if (path === '/' || path === `/${section}` || !path.includes('/', 1)) return 'section-home';

	const segments = path.split('/').filter(Boolean);

	// One slug past the section root is the detail page for that section; deeper
	// paths are a grouping within it (an artist's works, a release's lots).
	if (segments.length > 2) return 'collection';

	return config.detailTargets[section] ?? 'other';
};
